import React, { useState, useMemo } from 'react'
import { Box, CircularProgress, useMediaQuery, useTheme } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import Chart from '../../../chart-kit/shared/components/Chart'
import { getTraceName } from '../utils/getTraceName'
import { CustomLayout, ChartView, RechartData, TraceSuffix, LoadingState, CustomTrace } from '../../../chart-kit/shared/utils/chartModel'
import { useTypedSelector } from 'src/State/RootReducer'
import isNaN from 'lodash/isNaN'
import Backdrop from '@mui/material/Backdrop'
import useResizeObserver from 'use-resize-observer'
import { getChartName } from '../utils/SystemChartUtils'
import { Moment } from '@e3dc-react/shell/Libs/moment'
import { UnitType, UnitTypes } from 'src/Libs/types'
import { ChartSettings } from '../../../chart-kit/editor'
import SystemChartHeader from './SystemChartHeader'
import { cloneDeep } from 'lodash'
import { useChartDateBarContext, useXAxisTicks } from 'src/Libs/chart-kit'
import useZoomCharts from '../hooks/use-zoom-charts'
import { useChartDataAndCells, useLegendPayload } from 'src/Libs/chart-kit/pie-charts/hooks'
import moment from 'moment'
import { useWallboxHardwareByStorage } from 'src/Libs/wallboxes/hooks'

const useStyles = makeStyles()(theme => ({
  paper: {
    height: '100%',
    display: 'flex',
    paddingBottom: theme.spacing(2),
  },
  backdrop: {
    zIndex: 9999,
  },
  paperContent: {
    display: 'flex',
    flexDirection: 'column',
    flex: '0 1 auto',
  },
  wrapperClass: {
    height: '100%',
    maxHeight: 1200,
    width: '100%',
  },
}))

interface SystemChartProps {
  rechartData: RechartData
  view: ChartView
  resolution?: ChartView
  customLayout?: CustomLayout
  displayPlain?: boolean
  handleSaveChart?: (props: ChartSettings) => void
  handleDeleteChart?: (id: number | string) => void
  from: Moment
  to: Moment
  showForecast: boolean
  loadingState: LoadingState
  traceSuffixes?: TraceSuffix[]
  maxHeight?: number
  pieChartTooltipText?: string
  isDynamicPowerPriceEnabled: boolean /* this is a temporary condition added with AF-3237. The plan is to remove it after SW1 and SW2 align. */
  hideLegend?: boolean
  toggleHideLegend: () => void
  initialChartLayout?: CustomLayout
  dataQa?: string
  setBiggestLegendHeight?: (height: number) => void
  fixedLegendHeight?: number
  isCostSavingsPieChart?: boolean
  isAutarkyPieChart?: boolean
  pieChartLabelAction?: React.ReactElement
  isWeightedAveragePriceOn?: boolean
  weightedPrice?: number
  legendUnitType?: UnitTypes
}

const SystemChart: React.FC<SystemChartProps> = ({
  view,
  resolution,
  customLayout: customLayoutBeforeUpdate,
  displayPlain,
  handleSaveChart,
  handleDeleteChart,
  from,
  to,
  showForecast,
  rechartData,
  loadingState,
  traceSuffixes,
  maxHeight = 0,
  pieChartTooltipText = '',
  isDynamicPowerPriceEnabled,
  hideLegend = false,
  toggleHideLegend,
  initialChartLayout,
  dataQa,
  setBiggestLegendHeight,
  fixedLegendHeight,
  isCostSavingsPieChart = false,
  isAutarkyPieChart,
  pieChartLabelAction,
  isWeightedAveragePriceOn = false,
  weightedPrice,
  legendUnitType,
}) => {
  const customLayout: CustomLayout | undefined = useMemo(() => {
    /* in this block we make sure that a saved edited default chart, is reflecting the initialChartLayout traces */
    if (customLayoutBeforeUpdate?.isInitial && initialChartLayout) {
      const updatedTraces = initialChartLayout.traces.map(initialTrace => {
        const matchingCustomTrace = customLayoutBeforeUpdate?.traces.find((line: CustomTrace) => line.key === initialTrace.key)
        if (matchingCustomTrace) return { ...cloneDeep(matchingCustomTrace), alternateKey: initialTrace.alternateKey, unit: initialTrace.unit }
        return initialTrace
      })

      return {
        ...cloneDeep(customLayoutBeforeUpdate),
        traces: updatedTraces,
      }
    }
    return customLayoutBeforeUpdate
  }, [customLayoutBeforeUpdate, initialChartLayout])

  const { t } = useCustomTranslation()
  const { ref, width } = useResizeObserver<HTMLDivElement>({ box: 'border-box' })
  const { ref: headerRef, height: headerHeight } = useResizeObserver<HTMLDivElement>({ box: 'border-box' })
  const chartType = customLayout?.views[view]
  const isPieChart = chartType === 'pie'
  const theme = useTheme()
  const upMd = useMediaQuery(theme.breakpoints.up('md'))

  const { classes, cx } = useStyles()
  const [downloadLoading, setDownloadLoading] = useState(false)
  const { data: wallboxHardware } = useWallboxHardwareByStorage()
  const powermeterHardware = useTypedSelector(store => store.storage.powermeterHardware)
  const unit: UnitType = customLayout?.unit ?? UnitTypes.watt
  const summaryUnit = customLayout?.summaryUnit ?? UnitTypes.wattHour
  const secondUnit = customLayout?.secondUnit ?? ` ${UnitTypes.percentage}`
  const title = getChartName(customLayout, t)

  const traceNames = useMemo(() => {
    if (!customLayout) return {}
    return customLayout.traces.reduce((prev: Record<string, string>, customTrace) => {
      if (customTrace) {
        prev[customTrace.alternateKey ?? customTrace.key] = getTraceName(t, customTrace, wallboxHardware, powermeterHardware)
      }

      return prev
    }, {})
  }, [customLayout, t, wallboxHardware, powermeterHardware])

  const toggleLegendVisibility = (): void => {
    toggleHideLegend()
  }

  //if this is a chart with custom Sums created by the user, add entries to data with those sum values
  const customSums = customLayout?.traces.filter(trace => trace?.sumKeys?.length)

  if (rechartData.data && customSums?.length && rechartData.sums) {
    customSums.forEach(customSum => {
      let sumForLegend = 0
      rechartData.data!.forEach(dataPoint => {
        const sum = customSum.sumKeys?.reduce((prev, curr) => {
          if (!dataPoint[curr.key]) {
            return prev
          }
          if (curr.operand === 'add') {
            return prev + (dataPoint[curr.key] as number)
          } else {
            return prev - (dataPoint[curr.key] as number)
          }
        }, 0)
        if (sum && !isNaN(sum)) {
          sumForLegend += sum
          dataPoint[customSum.alternateKey!] = sum
        } else {
          dataPoint[customSum.alternateKey!] = 0
        }
      })
      rechartData.sums![customSum.alternateKey!] = {
        history: sumForLegend,
        forecast: null,
        customColor: customSum.color,
      }
    })
  }

  const impossibleTraces = useMemo(() => {
    const _impossibleTraces: string[] = []
    customLayout?.traces.forEach(customTrace => {
      if (customTrace?.key === 'Deration' && !showForecast) {
        // Deration is only supposed to be visible while "forecast" is shown
        _impossibleTraces.push(customTrace.key)
      }
      if (view === 'month' || view === 'year') {
        if (customTrace?.key === 'ASOC' || customTrace.key === 'ForecastPower') {
          _impossibleTraces.push(customTrace.key)
        }
      }
      if (view === 'week') {
        if (customTrace.key === 'ForecastPower') {
          _impossibleTraces.push(customTrace.key)
        }
      }
    })

    return _impossibleTraces
  }, [customLayout?.traces, showForecast, view])

  const sortedRechartData = rechartData.data?.sort((itemA, itemB) => {
    return (itemA.ts as number) - (itemB.ts as number)
  })

  const filteredData = useMemo(
    () => ({
      ...rechartData,
      data: sortedRechartData?.filter(item => item.forecast !== true),
    }),
    [rechartData, sortedRechartData]
  )

  const dataToShow = view === 'day' || view === 'week' || view === 'hour' ? { ...rechartData, data: sortedRechartData } : filteredData

  const {
    zoomedData,
    handleMouseDown: handleZoomMouseDown,
    handleMouseMove: handleZoomMouseMove,
    handleMouseUp: handleZoomMouseUp,
    resetZoom,
    zoomStartIndex,
    zoomEndIndex,
    zoomWasReset,
    isZoomedIn,
    zoomedFrom,
    zoomedTo,
  } = useZoomCharts({
    data: dataToShow.data ?? [],
  })
  const rechartDataWithZoomedData = {
    ...dataToShow,
    data: zoomedData,
  }
  const {
    data: piechartData,
    cells: pieChartCells,
    savingsSum,
  } = useChartDataAndCells({
    rechartData,
    customTraces: customLayout?.traces,
    impossibleTraces,
    traceNames,
    view,
    isDynamicPowerPriceEnabled,
    isCostSavingsPieChart,
    isAutarkyPieChart,
  })

  const { legendPayload } = useLegendPayload({ data: piechartData, upMd, isCostSavingsPieChart, unitType: legendUnitType })
  const { hasCustomRange } = useChartDateBarContext()

  const usedFrom = useMemo(() => (isZoomedIn ? moment(zoomedFrom) : from), [isZoomedIn, zoomedFrom, from])
  const usedTo = useMemo(() => (isZoomedIn ? moment(zoomedTo) : to), [isZoomedIn, zoomedTo, to])

  const {
    ticks: xAxisTicks,
    format: xAxisFormat,
    interval: xAxisInterval,
  } = useXAxisTicks(usedFrom, usedTo, resolution ?? view, isZoomedIn, hasCustomRange)

  const xAxisDomain = useMemo(() => {
    return [from.valueOf(), to.valueOf()]
  }, [from, to])

  if (displayPlain) {
    return (
      <Chart
        from={from}
        type={customLayout?.views[view] ?? 'bar'}
        unit={unit}
        summaryUnit={summaryUnit}
        minPrefix={customLayout?.minPrefix}
        secondUnit={secondUnit}
        xAxisDomain={xAxisDomain}
        xAxisFormat={xAxisFormat}
        xAxisInterval={xAxisInterval}
        rechartData={rechartDataWithZoomedData}
        customTraces={customLayout?.traces}
        impossibleTraces={impossibleTraces}
        showForecast={!!rechartData && showForecast}
        labelTraces={customLayout?.labelTraces}
        labelNameKey={customLayout?.labelNameKey}
        traceNames={traceNames}
        view={view}
        xAxisTicks={xAxisTicks}
        height={maxHeight}
        width={width}
        loadingState={loadingState}
        traceSuffixes={traceSuffixes}
        title={title}
        dataQa={dataQa}
        handleZoomMouseDown={handleZoomMouseDown}
        handleZoomMouseMove={handleZoomMouseMove}
        handleZoomMouseUp={handleZoomMouseUp}
        zoomStartIndex={zoomStartIndex}
        zoomEndIndex={zoomEndIndex}
        zoomWasReset={zoomWasReset}
        isCostSavingsPieChart={isCostSavingsPieChart}
      />
    )
  }

  return (
    <Box className={classes.wrapperClass}>
      <Box ref={ref} id={`chart-${customLayout?.title}-${customLayout?.id}`} className={cx(classes.paper, classes.paperContent)}>
        <SystemChartHeader
          isPieChart={isPieChart}
          title={title ?? ''}
          customLayout={customLayout}
          systemChartProps={{
            chartProps: {
              from,
              type: customLayout?.views[view] ?? 'bar',
              unit,
              summaryUnit,
              secondUnit,
              xAxisDomain,
              xAxisFormat,
              rechartData,
              customTraces: customLayout?.traces,
              impossibleTraces,
              showForecast: Boolean(rechartData && showForecast),
              traceNames,
              view,
              xAxisTicks: xAxisTicks,
              xAxisInterval,
              isInitial: customLayout?.isInitial,
            },
            views: customLayout?.views,
            name: customLayout?.isInitial ? t('systemDataHandler.presets.' + customLayout.title) : customLayout?.title,
            chartId: customLayout?.id,
            loadingState,
          }}
          handleSaveChart={handleSaveChart}
          toggleLegendVisibility={toggleLegendVisibility}
          hideLegend={hideLegend}
          setDownloadLoading={setDownloadLoading}
          handleDeleteChart={handleDeleteChart}
          resetZoom={resetZoom}
          isZoomedIn={isZoomedIn}
          ref={headerRef}
          tooltipText={pieChartTooltipText}
        />
        <Chart
          from={from}
          pieChartData={piechartData}
          pieChartLegendPayload={legendPayload}
          pieChartCells={pieChartCells}
          savingsSum={savingsSum}
          type={customLayout?.views[view] ?? 'bar'}
          unit={unit}
          summaryUnit={summaryUnit}
          minPrefix={customLayout?.minPrefix}
          labelTraces={customLayout?.labelTraces}
          labelNameKey={customLayout?.labelNameKey}
          secondUnit={secondUnit}
          xAxisDomain={xAxisDomain}
          xAxisFormat={xAxisFormat}
          xAxisInterval={xAxisInterval}
          rechartData={rechartDataWithZoomedData}
          customTraces={customLayout?.traces}
          impossibleTraces={impossibleTraces}
          showForecast={!!rechartData && showForecast}
          traceNames={traceNames}
          view={view}
          xAxisTicks={xAxisTicks}
          height={maxHeight}
          headerHeight={headerHeight}
          width={width}
          loadingState={loadingState}
          traceSuffixes={traceSuffixes}
          title={title}
          hideLegend={hideLegend}
          dataQa={dataQa}
          handleZoomMouseDown={handleZoomMouseDown}
          handleZoomMouseMove={handleZoomMouseMove}
          handleZoomMouseUp={handleZoomMouseUp}
          zoomStartIndex={zoomStartIndex}
          zoomEndIndex={zoomEndIndex}
          zoomWasReset={zoomWasReset}
          handleDoubleClick={resetZoom}
          setBiggestLegendHeight={setBiggestLegendHeight}
          fixedLegendHeight={fixedLegendHeight}
          isCostSavingsPieChart={isCostSavingsPieChart}
          isWeightedAveragePriceOn={isWeightedAveragePriceOn}
          weightedPrice={weightedPrice}
          pieChartLabelAction={pieChartLabelAction}
        />
      </Box>
      <Backdrop open={downloadLoading} className={classes.backdrop}>
        <CircularProgress />
      </Backdrop>
    </Box>
  )
}

export default SystemChart
